import { batchActions } from 'redux-batched-actions';

import { resetConversation, handleUserConversationsResponse } from './conversation';
import { disconnectFaye } from './faye';
import http from './http';
import { resetIntegrations } from './integrations';
import { setUser, resetUser, immediateUpdate as immediateUpdateUser } from './user';
import * as AuthActions from '../actionTypes/auth';
import { getClientId, getClientInfo } from '../utils/client';
import { removeItem, removeItems } from '../utils/storage';

export function login(userId, jwt) {
    return (dispatch, getState) => {
        const {
            config: { appId, integrationId },
            auth: { sessionToken },
            user: { _id },
        } = getState();

        // force update the current user before logging in another one
        const promise = _id ? dispatch(immediateUpdateUser()) : Promise.resolve();

        return promise
            .then(() => {
                const actions = [
                    setAuth({
                        jwt,
                    }),
                    setUser({
                        userId,
                    }),
                    resetConversation(),
                    resetIntegrations(),
                ];

                // order matters here
                // if faye disconnects before we clear the user from the state, the disconnected banner will appear
                dispatch(batchActions(actions));
                disconnectFaye();

                return dispatch(
                    http('POST', `/v2/apps/${appId}/login`, {
                        integrationId,
                        appUserId: _id,
                        userId,
                        sessionToken,
                        client: getClientInfo(integrationId),
                    }),
                );
            })
            .then((response) => {
                // get rid of session token
                removeItem(`${integrationId}.sessionToken`);

                if (response && Object.keys(response).length > 0) {
                    return dispatch(handleUserConversationsResponse(response, { shouldFetchMessages: true }));
                }
            });
    };
}

export function logout() {
    return (dispatch, getState) => {
        const {
            config: { appId, integrationId },
            user: { _id: appUserId, userId },
        } = getState();

        if (!userId) {
            return Promise.resolve();
        }

        let promise;
        if (appUserId) {
            promise = dispatch(
                http('POST', `/v2/apps/${appId}/appusers/${appUserId}/logout`, {
                    client: {
                        id: getClientId(integrationId),
                        integrationId,
                    },
                }),
            );
        } else {
            promise = Promise.resolve();
        }

        return promise.then(() => {
            const actions = [resetAuth(), resetUser(), resetConversation(), resetIntegrations()];

            // order matters here
            // if faye disconnects before we clear the user from the state, the disconnected banner will appear
            dispatch(batchActions(actions));
            disconnectFaye();

            removeItems(integrationId, ['appUserId', 'sessionToken']);
        });
    };
}

export function consumeAuthCode(authCode) {
    return (dispatch, getState) => {
        const {
            config: { appId, integrationId },
        } = getState();
        return dispatch(
            http('POST', `/v2/apps/${appId}/appusers/authcode`, {
                authCode,
                client: getClientInfo(integrationId),
            }),
        )
            .then((response) => response && response.appUser)
            .catch((err) => {
                if (err.code === 'invalid_auth') {
                    throw new Error('Invalid or expired authCode');
                } else {
                    throw err;
                }
            });
    };
}

export function setAuth(props) {
    return {
        type: AuthActions.SET_AUTH,
        ...props,
    };
}

export function resetAuth() {
    return {
        type: AuthActions.RESET_AUTH,
    };
}
